import { useEffect, useMemo } from "react";
import {
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import LocationSearchingIcon from "@material-ui/icons/LocationSearching";
import {
  useSettingsDispatch,
  BaseSetting,
  useSettingsState,
} from "../../context/settings";
import {
  useFieldsFiltersDispatch,
  useFieldsFiltersState,
} from "../../context/field-filters";

export const Filter = ({
  id,
  label,
  value,
  options,
  handleChange,
  color,
}: {
  id: string;
  label: string;
  value: string;
  options: string[];
  handleChange: (value: string) => void;
  color?: string;
}) => {
  const useStyles = makeStyles((theme) => ({
    root: {
      minWidth: 120,
      maxWidth: 190,
      marginRight: theme.spacing(2),
    },
    input: {
      color: color ? color : theme.palette.text.secondary,
      fontWeight: 700,
      fontSize: "0.9rem",
    },
  }));
  const classes = useStyles();

  return (
    <FormControl className={classes.root}>
      <InputLabel shrink id={`field-${id}-filter-label`}>
        {label}
      </InputLabel>
      <Select
        className={classes.input}
        labelId={`field-${id}-filter-label`}
        id={`field-${id}-filter`}
        value={value}
        onChange={(event) => handleChange(event.target.value as string)}
        displayEmpty
        disableUnderline
      >
        <MenuItem value="">None</MenuItem>
        {options.map((option, index) => (
          <MenuItem key={`field-${id}-filter-option-${index}`} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const optionFilter = (o: string | undefined): o is string => !!o?.trim();

const normaliseOptions = (rawOptions: (string | undefined)[]): string[] => {
  return Array.from(new Set(rawOptions.filter(optionFilter))).sort((a, b) =>
    a.toUpperCase().localeCompare(b.toUpperCase())
  );
};

const filterFields = ["farm", "crop_type", "valve_group"] as const;
type FilterKey = typeof filterFields[number];
const filterLabels: Record<FilterKey, string> = {
  farm: "Farm",
  crop_type: "Crop Type",
  valve_group: "Valve Group",
};
const FieldFilters = ({
  color,
  fields,
}: {
  color: string;
  fields: {
    id: number;
    client_id: number;
    name: string;
    farm?: string;
    crop_type?: string;
    valve_group?: string;
  }[];
}) => {
  const { fieldsSettings } = useSettingsState();
  const selection = useFieldsFiltersState();
  const settingsDispatch = useSettingsDispatch();
  const filtersDispatch = useFieldsFiltersDispatch();

  const options = useMemo(
    () =>
      Object.fromEntries(
        filterFields.map((fieldKey) => [
          fieldKey,
          normaliseOptions(
            fields
              .filter((field) => {
                for (const f of filterFields) {
                  if (f === fieldKey) continue;
                  if (selection[f] && field[f] !== selection[f]) return false;
                }
                return true;
              })
              .map((field) => field[fieldKey])
          ),
        ])
      ) as Record<FilterKey, string[]>,
    [fields, selection]
  );

  const useStyles = makeStyles((theme) => ({
    root: {
      display: "flex",
      alignItems: "center",
    },
    icon: {
      color: theme.palette.primary.dark,
      marginRight: theme.spacing(2),
    },
  }));
  const classes = useStyles();

  // add any missing fields to the settings
  useEffect(() => {
    if (fieldsSettings.visibility.length > 0) {
      const missingFields = fields.filter(
        (field) =>
          !fieldsSettings.visibility.find((item) => item.field_id === field.id)
      );
      if (missingFields.length > 0) {
        settingsDispatch({
          type: "fieldsVisibility",
          payload: [
            ...fieldsSettings.visibility,
            ...missingFields.map<BaseSetting>((field, idx) => ({
              id: `new_${idx}`,
              label: field.name,
              client_id: field.client_id,
              field_id: field.id,
              type: "field",
              hidden: 0,
              order: idx,
              ref: null,
            })),
          ],
        });
      }
    }
  }, [fields, filtersDispatch]);

  return (
    <div className={classes.root}>
      <LocationSearchingIcon className={classes.icon} />
      {filterFields.map((fieldKey) => (
        <Filter
          id={fieldKey}
          key={fieldKey}
          label={filterLabels[fieldKey]}
          value={selection[fieldKey]}
          options={options[fieldKey]}
          handleChange={(value) =>
            filtersDispatch({
              type: "fieldsFilterSelection",
              payload: { [fieldKey]: value },
            })
          }
          color={color}
        />
      ))}
    </div>
  );
};

export default FieldFilters;
